import _ from 'lodash';
import React, {
    ChangeEvent,
    FC,
    MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MarketSlug } from '../../../common/CountryMarketConfig';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Icon } from '../../../components/Icon';
import { Input } from '../../../components/Input';
import { InputGroup } from '../../../components/InputGroup';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import { useAppRoutes } from '../../../hooks/useAppRoutes';
import {
    getTaxonomyDisplayName,
    processingCapabilitiesPotentiallyRequiredFields,
} from '../../../lib/briefing';
import { noOp } from '../../../lib/utils';
import { AttachedFile, Price, Step, StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import { displayOption } from '../BriefingUtils';
import { isFieldAvailableDependingOnApplications } from '../StepProcessingCapabilities/StepProcessingCapabilities';
import { processingCapabilitiesFields } from '../StepProcessingCapabilities/fieldDependencies';

export interface ReviewTableCell {
  heading?: string;
  content: string | React.ReactNode | null;
}

export const StepReview: FC<StepProps> = ({ onValidate = noOp }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { briefingRoute } = useAppRoutes();

  const MAX_PRODUCT_NAME_LENGTH = 100;

  const [isProductNameValid, setIsProductNameValid] = useState(false);
  const isStepValid = useMemo(() => isProductNameValid, [isProductNameValid]);
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const [briefingState, { setProductName }] = useBriefingState();
  const {
    steps,
    branch,
    productName,
    targetConsumerAge,
    targetConsumerGender,
    productApplications,
    productApplicationsOther,
    productMarketingClaims,
    productMarketingClaimsOther,
    formulationType,
    formulationTypeOther,
    texture,
    textureOther,
    storage,
    storageOther,
    productForm,
    productFormOther,
    restrictionsMust,
    restrictionsMustOther,
    restrictionsCan,
    restrictionsCanOther,
    requirementsMust,
    requirementsMustOther,
    requirementsCan,
    requirementsCanOther,
    requirementsAggressiveComponents,
    requirementsLightStability,
    requirementsPHRange,
    requirementsApplicationConditionDetails,
    prices,
    additionalInfo,
    additionalInfoFiles,
    problem,
    azelisRawMaterials,
    rawMaterial,
    ingredientsFiles,
    activeFunctions,
    activeIngredients,
    packaging,
    packagingOther,
    colourShade,
    colourLabSupport,
    colourRequiredTime,
    colourSamples,
    fragranceCMR,
    fragranceConcept,
    fragranceDirection,
    fragranceLargerSample,
    fragranceMaxAmountAllergens,
    fragranceMust,
    fragranceMustOther,
    fragranceRequestedApplicationDosage,
    fragranceRequiredSampleTime,
    fragranceSamples,
    finishedProductSamples,
    finishedProductPotential,
    projectDeadline,
    requiredSampleTime,
    modifyBriefingIssue,
  } = briefingState;

  const [{ countryMarketConfig }] = useCountryMarketState();

  const onChangeProductName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(setProductName(event.target.value));
    },
    [dispatch, setProductName],
  );

  const goToStep = useCallback(
    (step: Step) => {
      navigate(`${briefingRoute}/${branch}/${step}`);
    },
    [branch, navigate, briefingRoute],
  );

  const createReviewTableElement = useCallback(
    (element: ReviewTableCell, index: number) => {
      if (element.heading) {
        return (
          <div className="w-full" key={index}>
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="mb-0 text-xl text-gray-600">
                {element.heading}
                <br />
                <span className="text-gray-700">
                  {element.content ? element.content : '-'}
                </span>
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        );
      } else {
        return (
          <div className="w-full" key={index}>
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="mb-0 text-xl text-gray-700">
                {element.content ? element.content : '-'}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        );
      }
    },
    [],
  );

  const createReviewField = useCallback(
    (
      complete: boolean,
      step: Step,
      value: ReviewTableCell[],
      stepNo: number,
      isDoubleGrid = true,
    ) => (
      <div
        key={stepNo}
        className="flex w-full pb-1 mt-4 border-b border-gray-400"
      >
        <div
          className={`w-6 mr-5 ${
            complete ? 'text-violet-300' : 'text-gray-600'
          }`}
        >
          <Icon name={complete ? 'CheckFilled' : 'None'} />
        </div>
        <div className="w-full mr-1">
          <div className="flex justify-between mb-3 items-top">
            <Heading level="h3" text={t(`topics:${step}:name`)} margin={0} />
            <button
              title={t('linksOrButtons:edit')}
              className="w-4 ml-1 text-blue-200 hover:text-blue-100"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                goToStep(step);
              }}
            >
              <Icon name="Edit" />
            </button>
          </div>
          <div className={`grid gap-8 font-regular ${isDoubleGrid ? 'md:grid-cols-2' : ''}`}>
            {complete && value
              ? value.map((element, index) =>
                  createReviewTableElement(element, index),
                )
              : '-'}
          </div>
        </div>
      </div>
    ),
    [goToStep, t, createReviewTableElement],
  );

  const getFileList = useCallback((fileContainer: AttachedFile[]) => {
    return (
      <ul>
        {fileContainer.map((file: AttachedFile, index: number) => (
          <li key={index} className="flex mt-2">
            <div className="w-6 mr-1">
              <Icon name="File" />
            </div>
            <div>{file.adjustedName}</div>
          </li>
        ))}
      </ul>
    );
  }, []);

  const processingCapabilitiesFieldsToDisplay = useMemo(
    () =>
      processingCapabilitiesFields.filter((field) =>
        isFieldAvailableDependingOnApplications(productApplications, field),
      ),
    [productApplications],
  );

  const isProcessingCapabilitiesStepComplete = useMemo(
    () =>
      !processingCapabilitiesFieldsToDisplay.some(
        (field) =>
          processingCapabilitiesPotentiallyRequiredFields.indexOf(field) > -1 &&
          !(briefingState[field] || briefingState[field] === false),
      ),
    [processingCapabilitiesFieldsToDisplay, briefingState],
  );

  const stepFields = useMemo(() => {
    return steps.map((step: Step, index: number) => {
      switch (step) {
        case 'modification-issue':
          return createReviewField(
            !!modifyBriefingIssue,
            step,
            [
              {
                heading: t('topics:problem:fix:name'),
                content: modifyBriefingIssue ? modifyBriefingIssue : null,
              },
            ],
            index,
          );
        case 'application': {
          const productApplicationsReviewArr = productApplications.map(
            (app, index) => ({
              heading: t(`topics:application:levels:${index}:selected`),
              content: getTaxonomyDisplayName(
                app,
                productApplicationsOther,
                t('labels:other'),
              ),
            }),
          );

          const activeFunctionsContent = {
            heading: t('topics:active-function:heading:review'),
            content: (activeFunctions ?? [])
              .filter(Boolean)
              .map((func) => t(`topics:active-function:${func}:name`))
              .join(', '),
          };
          const activeIngredientsContent = {
            heading: `${t('topics:active-ingredients:name')}, ${t(
              'topics:active-ingredients:concentration',
            )}`,
            content: _.values(activeIngredients ?? [])
              .filter((entry) => Boolean(entry))
              .map(
                ({ ingredient, concentration }) =>
                  `${ingredient?.name ?? 'NONAME'}, ${concentration} g/L`,
              )

              .join('; '),
          };

          const marketDependantContent = () => {
            if (
              countryMarketConfig?.eLabMarketSlug ===
              MarketSlug.AgriEnvironmental
            ) {
              return [
                ...productApplicationsReviewArr,
                activeFunctionsContent,
                activeIngredientsContent,
              ];
            } else {
              return productApplicationsReviewArr;
            }
          };

          return createReviewField(
            !!productApplications || !!productApplicationsOther,
            step,
            marketDependantContent(),
            index,
          );
        }
        case 'target-consumer':
          return createReviewField(
            !!targetConsumerAge || !!targetConsumerGender,
            step,
            [
              {
                heading: t(`topics:target-consumer:age`),
                content: (targetConsumerAge ?? [])
                  .filter((age) => age)
                  .join(', '),
              },
              {
                heading: t(`topics:target-consumer:gender`),
                content: targetConsumerGender,
              },
            ],
            index,
          );
        case 'formulation-attributes':
          // @FIXME: remove duplication and find a more general approach
          switch (countryMarketConfig?.eLabMarketSlug) {
            case 'FoodNutrition':
              return createReviewField(
                !!productMarketingClaims ||
                  !!productMarketingClaimsOther ||
                  !!storage ||
                  !!storageOther,
                step,
                [
                  {
                    heading: t(
                      `topics:formulation-attributes:productMarketingClaims`,
                    ),
                    content: displayOption(
                      productMarketingClaims,
                      productMarketingClaimsOther,
                    ),
                  },
                  {
                    heading: t(`topics:formulation-attributes:storage`),
                    content: displayOption(storage, storageOther),
                  },
                ],
                index,
              );

            case 'HomeCareIndustrialCleaning':
              return createReviewField(
                !!productMarketingClaims ||
                  !!productMarketingClaimsOther ||
                  !!productForm ||
                  !!productFormOther,
                step,
                [
                  {
                    heading: t(
                      `topics:formulation-attributes:productMarketingClaims`,
                    ),
                    content: displayOption(
                      productMarketingClaims,
                      productMarketingClaimsOther,
                    ),
                  },
                  {
                    heading: t(`topics:formulation-attributes:productForm`),
                    content: displayOption(productForm, productFormOther),
                  },
                ],
                index,
              );

            default:
              // THIS IS FOR PERSONAL CARE ONLY which was the first market segment we did
              return createReviewField(
                !!productMarketingClaims ||
                  !!formulationType ||
                  !!formulationTypeOther ||
                  !!texture ||
                  !!textureOther,
                step,
                [
                  {
                    heading: t(
                      `topics:formulation-attributes:productMarketingClaims`,
                    ),
                    content: displayOption(
                      productMarketingClaims,
                      productMarketingClaimsOther,
                    ),
                  },
                  {
                    heading: t(`topics:formulation-attributes:formulationType`),
                    content: displayOption(
                      formulationType,
                      formulationTypeOther,
                    ),
                  },
                  {
                    heading: t(`topics:formulation-attributes:texture`),
                    content: displayOption(texture, textureOther),
                  },
                ],
                index,
              );
          }

        case 'processing-capabilities':
          return createReviewField(
            isProcessingCapabilitiesStepComplete,
            step,
            processingCapabilitiesFieldsToDisplay.map((field) => ({
              heading: t(`topics:processing-capabilities:${field}`),
              // @INVESTIGATE: the simpler approaches didn't work, but why?
              content: [
                briefingState[field]
                  ? briefingState[field]
                  : briefingState[field] === false
                  ? t('placeholders:notApplicable')
                  : '-',
              ],
            })),
            index,
          );
        case 'colour':
          return createReviewField(
            !!colourShade ||
              !!colourSamples ||
              !!colourRequiredTime ||
              !!colourLabSupport,
            step,
            [
              {
                heading: t('topics:colour:colourShades'),
                content: _.isArray(colourShade)
                  ? displayOption(colourShade)
                  : colourShade,
              },
              {
                heading: t('topics:colour:colourSamples:label'),
                content: colourSamples ? colourSamples : null,
              },
              {
                heading: t('topics:colour:requiredTime:label'),
                content: colourRequiredTime ? colourRequiredTime : null,
              },
              {
                heading: t('topics:colour:labSupport:label'),
                content: colourLabSupport ? colourLabSupport : null,
              },
            ],
            index,
          );
        case 'fragrance':
          return createReviewField(
            !!fragranceConcept ||
              !!fragranceDirection ||
              !!fragranceMust ||
              !!fragranceMustOther ||
              !!fragranceCMR ||
              !!fragranceMaxAmountAllergens ||
              !!fragranceRequestedApplicationDosage ||
              !!fragranceSamples,
            step,
            [
              {
                heading: t('topics:fragrance:colourOrConcept'),
                content: _.isArray(fragranceConcept)
                  ? displayOption(fragranceConcept)
                  : fragranceConcept,
              },
              {
                heading: t('topics:fragrance:direction'),
                content: _.isArray(fragranceDirection)
                  ? displayOption(fragranceDirection)
                  : fragranceDirection,
              },
              {
                heading: t('topics:fragrance:mustComply'),
                content: _.isArray(fragranceMust)
                  ? displayOption(fragranceMust, fragranceMustOther)
                  : fragranceMust,
              },
              {
                heading: t('topics:fragrance:CMR'),
                content: _.isArray(fragranceCMR)
                  ? displayOption(fragranceCMR)
                  : fragranceCMR,
              },
              {
                heading: t('topics:fragrance:maxAllergensOnLabel'),
                content: fragranceMaxAmountAllergens
                  ? fragranceMaxAmountAllergens
                  : null,
              },
              {
                heading: t('topics:fragrance:requestedApplicationDosage'),
                content: fragranceRequestedApplicationDosage
                  ? fragranceRequestedApplicationDosage
                  : null,
              },
              {
                heading: t('topics:fragrance:fragranceSamples'),
                content: fragranceSamples ? fragranceSamples : null,
              },
              {
                heading: t('topics:fragrance:requiredSampleTime:label'),
                content: fragranceRequiredSampleTime
                  ? fragranceRequiredSampleTime
                  : null,
              },
              {
                heading: t('topics:fragrance:largerFragranceSize:label'),
                content: fragranceLargerSample ? fragranceLargerSample : null,
              },
            ],
            index,
          );
        case 'restrictions':
          return createReviewField(
            !!restrictionsMust ||
              !!restrictionsMustOther ||
              !!restrictionsCan ||
              !!restrictionsCanOther ||
              !!requirementsMust ||
              !!requirementsMustOther ||
              !!requirementsCan ||
              !!requirementsCanOther,
            step,
            [
              {
                heading: t(`topics:restrictions:requirementsMust`),
                content: displayOption(requirementsMust, requirementsMustOther),
              },
              {
                heading: t(`topics:restrictions:requirementsCan`),
                content: displayOption(requirementsCan, requirementsCanOther),
              },
              {
                heading: t(`topics:restrictions:restrictionsMust`),
                content: displayOption(restrictionsMust, restrictionsMustOther),
              },
              {
                heading: t(`topics:restrictions:restrictionsCan`),
                content: displayOption(restrictionsCan, restrictionsCanOther),
              },
            ],
            index,
          );
        case 'requirements': {
          const phValues = _.values(requirementsPHRange);
          const phRangeContent = phValues.length
            ? `${phValues[0]} - ${phValues[1]}`
            : null;

          return createReviewField(
            !!requirementsMust ||
              !!requirementsMustOther ||
              !!requirementsCan ||
              !!requirementsCanOther ||
              !!requirementsAggressiveComponents ||
              !!requirementsLightStability,
            step,
            [
              {
                heading: t('topics:requirements:requirementsMust'),
                content: displayOption(requirementsMust, requirementsMustOther),
              },
              {
                heading: t('topics:requirements:requirementsCan'),
                content: displayOption(requirementsCan, requirementsCanOther),
              },
              {
                heading: t(
                  'topics:requirements:applicationConditionDetails:label',
                ),
                content: requirementsApplicationConditionDetails
                  ? requirementsApplicationConditionDetails
                  : null,
              },
              {
                heading: t('topics:requirements:pHRange:label'),
                content: phRangeContent ? phRangeContent : null,
              },
              {
                heading: t('topics:requirements:aggressiveComponents:label'),
                content: requirementsAggressiveComponents
                  ? requirementsAggressiveComponents
                  : null,
              },
              {
                heading: t('topics:requirements:lightStability:label'),
                content: requirementsLightStability
                  ? requirementsLightStability
                  : null,
              },
            ],
            index,
          );
        }
        case 'price': {
          const displayPrice = (priceValue?: Price) =>
            `${priceValue?.min || 0} - ${priceValue?.max || 'n.a.'} ${
              countryMarketConfig?.currency
            } / ${countryMarketConfig?.weightUnit}`;

          const priceKeys = _.keys(prices);
          const priceValues = _.values(prices);
          const priceEntries = _.entries(prices);

          const content =
            priceKeys.length === 1 && priceKeys[0] === 'base'
              ? [
                  {
                    content: displayPrice(priceValues[0]),
                  },
                ]
              : priceEntries.map(([key, priceValue]) => ({
                  heading: t([
                    `topics:price:heading:${key}`,
                    `topics:price:heading:general`,
                  ]),
                  content: displayPrice(priceValue),
                }));

          return createReviewField(!!priceKeys.length, step, content, index);
        }
        case 'product-details':
          return createReviewField(
            !!finishedProductSamples ||
              !!finishedProductPotential ||
              !!projectDeadline,
            step,
            [
              {
                heading: t(
                  'topics:product-details:finishedProductSamples:heading',
                ),
                content: finishedProductSamples ? finishedProductSamples : null,
              },
              {
                heading: t(
                  'topics:product-details:finishedProductSamples:requiredTimeForSample',
                ),
                content: requiredSampleTime ? requiredSampleTime : null,
              },
              {
                heading: t('topics:product-details:finishedProductPotential'),
                content: finishedProductPotential
                  ? finishedProductPotential
                  : null,
              },
              {
                heading: t('topics:product-details:projectDeadline'),
                content: projectDeadline ? projectDeadline : null,
              },
            ],
            index,
          );
        case 'additional-info':
          return createReviewField(
            !!additionalInfo ||
              (!!additionalInfoFiles && !!additionalInfoFiles?.length),
            step,
            [
              {
                heading: t(`labels:notes`),
                content: additionalInfo ? additionalInfo : null,
              },
              {
                heading: t(`labels:attachments`),
                content:
                  additionalInfoFiles && additionalInfoFiles?.length > 0 ? (
                    <>{getFileList(additionalInfoFiles)}</>
                  ) : null,
              },
            ],
            index,
          );
        case 'problem':
          return createReviewField(
            !!problem,
            step,
            [{ content: problem }],
            index,
          );

        case 'packaging':
          return createReviewField(
            !!packaging || !!packagingOther,
            step,
            [
              {
                content: displayOption(packaging, packagingOther),
              },
            ],
            index,
          );
        case 'ingredients':
          return createReviewField(
            !!rawMaterial || !!ingredientsFiles || !!azelisRawMaterials,
            step,
            [
              {
                heading: t('topics:ingredients:azelisRawMaterials'),
                content:
                  azelisRawMaterials.length > 0 ? (
                    <span className="whitespace-pre-line">
                      {azelisRawMaterials.map(({ name }) => name).join('\n')}
                    </span>
                  ) : null,
              },
              {
                heading: t('topics:ingredients:rawMaterials'),
                content: rawMaterial,
              },
              {
                heading: t('labels:attachments'),
                content:
                  ingredientsFiles && ingredientsFiles?.length > 0 ? (
                    <>{getFileList(ingredientsFiles)}</>
                  ) : null,
              },
            ],
            index,
          );
        default:
          return null;
      }
    });
  }, [
    steps,
    createReviewField,
    productApplications,
    productApplicationsOther,
    targetConsumerAge,
    targetConsumerGender,
    t,
    countryMarketConfig,
    productMarketingClaims,
    formulationType,
    formulationTypeOther,
    texture,
    textureOther,
    activeFunctions,
    activeIngredients,
    productMarketingClaimsOther,
    isProcessingCapabilitiesStepComplete,
    processingCapabilitiesFieldsToDisplay,
    restrictionsMust,
    restrictionsMustOther,
    restrictionsCan,
    restrictionsCanOther,
    requirementsMust,
    requirementsMustOther,
    requirementsCan,
    requirementsCanOther,
    requirementsAggressiveComponents,
    requirementsLightStability,
    requirementsPHRange,
    requirementsApplicationConditionDetails,
    prices,
    additionalInfo,
    additionalInfoFiles,
    getFileList,
    problem,
    packaging,
    packagingOther,
    rawMaterial,
    ingredientsFiles,
    azelisRawMaterials,
    storage,
    storageOther,
    productForm,
    productFormOther,
    briefingState,
    colourLabSupport,
    colourRequiredTime,
    colourSamples,
    colourShade,
    fragranceLargerSample,
    fragranceMaxAmountAllergens,
    fragranceMust,
    fragranceMustOther,
    fragranceRequestedApplicationDosage,
    fragranceRequiredSampleTime,
    fragranceSamples,
    fragranceCMR,
    fragranceConcept,
    fragranceDirection,
    finishedProductSamples,
    finishedProductPotential,
    projectDeadline,
    requiredSampleTime,
    modifyBriefingIssue,
  ]);

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading level="h1" margin={0} text={t(`components:review:heading`)} />
      <div className="w-full md:w-8/12">
        <InputGroup label={t('components:review:productName')}>
          <div className="text-5xl font-medium">
            <Input
              autofocus={true}
              onChange={onChangeProductName}
              onValidate={setIsProductNameValid}
              placeholder={t('labels:enterAName')}
              initialValue={productName}
              variant="transparent"
              fontWeight="font-medium"
              maxLength={MAX_PRODUCT_NAME_LENGTH}
            />
          </div>
        </InputGroup>
        {stepFields}
      </div>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
